import React from "react";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { ImageBackground, View, Text, TouchableHighlight, GestureResponderEvent } from "react-native";
import { GlobalStyles } from "../../style/Style";
import { MediaQueries } from "../../hooks/dynamicStyleHook";
import { useServices } from "../../../contexts/ServiceContext";

interface GameProps {
    imageUri: string;
    title: string;
    timeLine: string;
    prizeString?: string;
    ctaLabel: string;
    onPress: (event: GestureResponderEvent) => void;
    onMyPicksScreen?: boolean;
    last?: boolean;
}

export const GameButton: React.FC<GameProps> = ({
    imageUri,
    title,
    timeLine,
    prizeString,
    ctaLabel,
    onPress,
    onMyPicksScreen,
    last,
}) => {
    const { edgeDeploy } = useServices();
    const styles = useDynamicStyles(generateStyles);
    const cashContainer = prizeString ? (
        <View style={[edgeDeploy ? styles.edgeCashContainer : styles.cashContainer]}>
            <Text style={styles.cash}>{prizeString}</Text>
        </View>
    ) : (
        <></>
    );

    return (
        <TouchableHighlight
            onPress={onPress}
            underlayColor={"none"}
            style={[
                edgeDeploy ? styles.edgeParent : styles.parent,
                last ? { marginBottom: 0 } : {},
                edgeDeploy && onMyPicksScreen && styles.edgeMyPicksParent,
            ]}
        >
            <View
                style={[
                    edgeDeploy ? styles.edgeContainer : styles.container,
                    edgeDeploy && onMyPicksScreen && styles.edgeMyPicksContainer,
                    onMyPicksScreen && styles.roundBottom,
                ]}
            >
                {cashContainer}
                <View style={styles.gameContainer}>
                    <ImageBackground source={{ uri: imageUri }} style={styles.background}>
                        <div className="gameButtonGradient">
                            <Text style={styles.title}>{title}</Text>
                            <Text style={styles.timeLine}>{timeLine}</Text>
                        </div>
                    </ImageBackground>
                </View>
                {(!onMyPicksScreen || edgeDeploy) && (
                    <View style={[styles.ctaContainer, edgeDeploy ? styles.edgeCtaContainer : {}]}>
                        <View style={styles.ctaTextContainer}>
                            <Text style={styles.cta}>{ctaLabel}</Text>
                        </View>
                    </View>
                )}
            </View>
        </TouchableHighlight>
    );
};

const generateStyles = () => {
    return EStyleSheet.create({
        [MediaQueries.MEDIUM]: {
            edgeParent: {
                width: "50%",
            },
        },
        [MediaQueries.XSMALL]: {
            edgeMyPicksParent: {
                width: "100%",
            },
            edgeParent: {
                width: "100%",
                padding: 7.5,
            },
            edgeContainer: {
                height: 180,
                minHeight: 180,
            },
        },
        edgeParent: {
            width: "33.333%",
            padding: 15,
        },
        edgeMyPicksParent: {
            width: "50%",
        },
        edgeContainer: {
            borderRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            overflow: "hidden",
            flex: 1,
            height: 245,
            minHeight: 245,
            flexDirection: "column",
            position: "relative",
        },
        edgeMyPicksContainer: {
            height: 190,
            minHeight: 190,
        },
        edgeCtaContainer: {
            height: 40,
        },
        edgeCashContainer: {
            position: "absolute",
            height: 60,
            width: 100,
            top: 0,
            right: 0,
            borderBottomLeftRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderTopRightRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.CASH_BACKGROUND),
        },
        edgeCashLabel: {
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            fontSize: 18,
            lineHeight: 18,
            color: () => EStyleSheet.value(GlobalStyles.CASH_TEXT),
        },
        parent: {
            marginBottom: 10,
        },
        "parent:last-child": {
            marginBottom: 0,
        },
        container: {
            borderTopLeftRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderTopRightRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            overflow: "hidden",
            flex: 1,
            height: 130,
            minHeight: 130,
            flexDirection: "column",
        },
        roundBottom: {
            borderBottomLeftRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderBottomRightRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            height: 100,
            minHeight: 100,
        },
        background: {
            height: "100%",
        },
        gameContainer: {
            width: "100%",
            flex: 1,
            height: 100,
        },
        ctaContainer: {
            width: "100%",
            height: 30,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.ACCENT_COLOUR),
            borderBottomLeftRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderBottomRightRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
        },
        cashContainer: {
            position: "absolute",
            height: 30,
            width: 160,
            top: 0,
            right: 0,
            borderBottomLeftRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderTopRightRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            backgroundColor: () => EStyleSheet.value(GlobalStyles.CASH_BACKGROUND),
        },
        cash: {
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            fontSize: 20,
            color: () => EStyleSheet.value(GlobalStyles.CASH_TEXT),
        },
        cta: {
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            fontSize: 22,
            marginTop: 2,
            lineHeight: 30,
            textAlign: "center",
            color: () => EStyleSheet.value(GlobalStyles.GAME_CTA_TEXT),
        },
        ctaTextContainer: {
            flex: 1,
            flexGrow: 1,
            alignItems: "center",
            justifyContent: "center",
        },
        title: {
            width: "100%",
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            fontSize: 24,
            lineHeight: 24,
            margin: 0,
            marginTop: "auto",
            color: () => EStyleSheet.value(GlobalStyles.GAME_TITLE_TEXT),
        },
        timeLine: {
            width: "100%",
            fontFamily: () => EStyleSheet.value(GlobalStyles.BASE_FONT),
            fontSize: 10,
            lineHeight: 10,
            margin: 0,
            color: () => EStyleSheet.value(GlobalStyles.GAME_DATE_TEXT),
            fontWeight: 600,
        },
    });
};
